// Audio analysis: live microphone input split into low/mid/high energy bands (0..1, smoothed),
// mirroring the lowE/midE/highE values that drove most audio-reactive effects in NEU.pde.
// Falls back to a synthetic, time-based "breathing" energy signal if the microphone is
// unavailable or the user declines permission, so the piece still feels alive either way.

export class AudioAnalyser {
  constructor() {
    this.low = 0;
    this.mid = 0;
    this.high = 0;
    this.ready = false;
    this.usingMic = false;

    // --- Extended analysis (drives the beat-synced / auto-mix / bioluminescence features) ---
    // Finer 5-band split on top of the classic low/mid/high, so colour effects can map distinct
    // frequency regions to distinct hues instead of everything reacting to the same number.
    this.sub = 0; // deepest bass - kick drum body
    this.bass = 0;
    this.lowMid = 0;
    this.highMid = 0;
    this.treble = 0;

    this.level = 0; // overall energy 0..1, smoothed
    this.flux = 0; // spectral flux (broadband onset strength) 0..1
    this.beat = false; // true only on the single frame a beat onset was detected
    this.beatPulse = 0; // 1.0 on beat, decays to 0 - use this for visual punch
    this.beatCount = 0;
    this.bpm = 0; // rough running estimate from recent beat intervals

    this._ctx = null;
    this._analyser = null;
    this._freqData = null;
    this._prevSpectrum = null;
    this._fallbackT = Math.random() * 1000;

    this._energyHistory = [];
    this._beatCooldown = 0;
    this._lastBeatAt = 0;
    this._elapsed = 0;
    this._beatIntervals = [];
  }

  async start() {
    try {
      // Fast timeout (1.5s) so the engine boots immediately without waiting on slow/unanswered permission prompts
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("mic timeout")), 1500));
      const constraints = {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      };
      const stream = await Promise.race([navigator.mediaDevices.getUserMedia(constraints), timeout]);
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Some browsers (notably Safari) can create a context in "suspended" state even from
      // within a user-gesture click handler - explicitly resume so analysis actually starts.
      if (this._ctx.state === "suspended") {
        await this._ctx.resume().catch(() => {});
      }
      const source = this._ctx.createMediaStreamSource(stream);
      this._analyser = this._ctx.createAnalyser();
      this._analyser.fftSize = 1024;
      this._analyser.smoothingTimeConstant = 0.55;
      source.connect(this._analyser);
      this._freqData = new Uint8Array(this._analyser.frequencyBinCount);
      this._prevSpectrum = new Uint8Array(this._analyser.frequencyBinCount);
      this._computeBands();
      this.usingMic = true;
      this.ready = true;
    } catch (err) {
      console.warn("[STARCODE] Mikrofon nicht verfuegbar, nutze synthetische Audio-Energie:", err.message);
      this.usingMic = false;
      this.ready = true;
    }
  }

  _computeBands() {
    const sRate = this._ctx?.sampleRate || 44100;
    const binHz = sRate / 1024; // ~43 Hz per bin
    const hzToBin = (hz) => Math.max(0, Math.min(511, Math.round(hz / binHz)));
    this._bands = {
      sub: [hzToBin(20), Math.max(1, hzToBin(60))],
      bass: [hzToBin(60), Math.max(2, hzToBin(250))],
      low: [hzToBin(20), Math.max(2, hzToBin(250))],
      lowMid: [hzToBin(250), Math.max(3, hzToBin(800))],
      mid: [hzToBin(250), Math.max(4, hzToBin(2200))],
      highMid: [hzToBin(800), Math.max(5, hzToBin(3500))],
      treble: [hzToBin(3500), Math.max(6, hzToBin(10000))],
      high: [hzToBin(2200), Math.max(6, hzToBin(10000))],
      flux: [hzToBin(30), Math.max(8, hzToBin(10000))],
    };
  }

  update(dt) {
    this._elapsed += dt;

    if (this.usingMic && this._analyser) {
      this._analyser.getByteFrequencyData(this._freqData);
      if (!this._bands) this._computeBands();
      const b = this._bands;

      const rawLow = this._avg(this._freqData, b.low[0], b.low[1]);
      const rawMid = this._avg(this._freqData, b.mid[0], b.mid[1]);
      const rawHigh = this._avg(this._freqData, b.high[0], b.high[1]);

      const rawSub = this._avg(this._freqData, b.sub[0], b.sub[1]);
      const rawBass = this._avg(this._freqData, b.bass[0], b.bass[1]);
      const rawLowMid = this._avg(this._freqData, b.lowMid[0], b.lowMid[1]);
      const rawHighMid = this._avg(this._freqData, b.highMid[0], b.highMid[1]);
      const rawTreble = this._avg(this._freqData, b.treble[0], b.treble[1]);

      // Dynamic Auto-Gain normaliser: fast attack, gentle release
      const currentPeak = Math.max(rawLow, rawMid, rawHigh, rawSub, rawBass, 0.05);
      if (!this._agcPeak) this._agcPeak = 0.15;
      if (currentPeak > this._agcPeak) {
        this._agcPeak += (currentPeak - this._agcPeak) * 0.25;
      } else {
        this._agcPeak -= this._agcPeak * (dt * 0.04);
        if (this._agcPeak < 0.08) this._agcPeak = 0.08;
      }
      const norm = 1.0 / this._agcPeak;
      const shape = (v) => Math.min(1.5, Math.pow(v * norm, 1.2));

      const smooth = 0.32;
      this.low += (shape(rawLow) - this.low) * smooth;
      this.mid += (shape(rawMid) - this.mid) * smooth;
      this.high += (shape(rawHigh) - this.high) * smooth;

      const bSmooth = 0.35;
      this.sub += (shape(rawSub) - this.sub) * bSmooth;
      this.bass += (shape(rawBass) - this.bass) * bSmooth;
      this.lowMid += (shape(rawLowMid) - this.lowMid) * bSmooth;
      this.highMid += (shape(rawHighMid) - this.highMid) * bSmooth;
      this.treble += (shape(rawTreble) - this.treble) * bSmooth;

      // Spectral flux: transient/onset detection across active bins
      if (this._prevSpectrum) {
        let flux = 0;
        const [fStart, fEnd] = b.flux;
        for (let i = fStart; i < fEnd; i++) {
          const diff = this._freqData[i] / 255 - this._prevSpectrum[i] / 255;
          if (diff > 0) flux += diff;
        }
        flux = Math.min(1.5, flux / Math.max(1, (fEnd - fStart) * 0.035));
        this.flux += (flux - this.flux) * 0.45;
      }
      this._prevSpectrum.set(this._freqData);
    } else {
      this._fallbackT += dt;
      const t = this._fallbackT;
      // Rich synthetic multi-band oscillation for offline / test preview
      this.low = 0.45 + 0.45 * Math.max(0, Math.sin(t * 1.8));
      this.mid = 0.35 + 0.38 * Math.max(0, Math.sin(t * 2.5 + 1.2));
      this.high = 0.3 + 0.35 * Math.max(0, Math.sin(t * 3.8 + 2.4));
      this.sub = 0.4 + 0.5 * Math.max(0, Math.sin(t * 1.8 - 0.2));
      this.bass = this.low;
      this.lowMid = this.mid;
      this.highMid = 0.35 + 0.4 * Math.max(0, Math.sin(t * 3.2));
      this.treble = this.high;
      this.flux = Math.max(0, Math.sin(t * 3.6)) * 0.85;
    }

    this.level += ((this.low + this.mid + this.high) / 3 - this.level) * 0.25;
    this._detectBeat(dt);
  }

  // Adaptive-threshold onset detection on the low band: a beat is a local energy spike that
  // clearly exceeds the recent running average, rate-limited so a single kick can't retrigger.
  _detectBeat(dt) {
    this.beat = false;
    this._beatCooldown -= dt;

    const energy = this.low + this.flux * 0.5;
    this._energyHistory.push(energy);
    if (this._energyHistory.length > 60) this._energyHistory.shift();

    if (this._energyHistory.length >= 24) {
      let sum = 0;
      for (const e of this._energyHistory) sum += e;
      const avg = sum / this._energyHistory.length;

      // Variance-adaptive sensitivity: on very steady material a smaller relative spike still
      // counts, on dynamic material we demand a bigger one. Lowered multiplier so real music beats
      // and synthetic transients trigger reliably.
      let varSum = 0;
      for (const e of this._energyHistory) varSum += (e - avg) * (e - avg);
      const variance = varSum / this._energyHistory.length;
      const threshold = avg * (1.10 + Math.min(0.35, variance * 8.0));

      const isKickSpike = energy > threshold && energy > 0.08;
      const isFluxSpike = this.flux > 0.28;

      if ((isKickSpike || isFluxSpike) && this._beatCooldown <= 0) {
        this.beat = true;
        this.beatCount++;
        this.beatPulse = 1.0;
        this._beatCooldown = 0.15; // hard rate limit ~ max 400 BPM

        const interval = this._elapsed - this._lastBeatAt;
        if (interval > 0.18 && interval < 2.0) {
          this._beatIntervals.push(interval);
          if (this._beatIntervals.length > 8) this._beatIntervals.shift();
          const median = [...this._beatIntervals].sort((a, b) => a - b)[Math.floor(this._beatIntervals.length / 2)];
          this.bpm = Math.round(60 / median);
        }
        this._lastBeatAt = this._elapsed;
      }
    }

    this.beatPulse = Math.max(0, this.beatPulse - dt * 2.5);
  }

  _avg(arr, start, end) {
    let sum = 0;
    const count = Math.max(1, end - start);
    for (let i = start; i < end; i++) sum += arr[i];
    return sum / count / 255;
  }
}
